import React, { useState, useEffect } from 'react';
import { adminApiService as apiService } from '../../services/adminApiService';
import { useAuth } from '../../context/AuthContext';
import { ApiError } from '../../types/errorTypes';
import CommissionTierEditor from './CommissionTierEditor';
import RuleChangeImpactAnalyzer from './RuleChangeImpactAnalyzer';
import RuleHistoryViewer from './RuleHistoryViewer';

interface BusinessRule {
  report_type: string;
  commission_tiers: CommissionTier[];
  fees: {
    manager_fee_percent: number;
    company_overhead_percent: number;
  };
  active: boolean;
  effective_date: string;
  version: string;
}

interface CommissionTier {
  id?: number;
  min_margin: number;
  max_margin: number;
  commission_percent: number;
}

interface RuleChanges {
  commission_tiers?: CommissionTier[];
  fees?: {
    manager_fee_percent?: number;
    company_overhead_percent?: number;
  };
}

const BusinessRulesManager: React.FC = () => {
  const { user } = useAuth();
  const [reportType, setReportType] = useState<'exteriors' | 'cody'>('exteriors');
  const [currentRules, setCurrentRules] = useState<BusinessRule | null>(null);
  const [pendingChanges, setPendingChanges] = useState<RuleChanges>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showImpactAnalysis, setShowImpactAnalysis] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [effectiveDate, setEffectiveDate] = useState<string>(
    new Date().toISOString().split('T')[0]
  );

  useEffect(() => {
    fetchCurrentRules();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportType]);

  const fetchCurrentRules = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.get(`/v2/admin/business-rules?report_type=${reportType}`);
      setCurrentRules(response.data);
    } catch (err) {
      const error = err as ApiError;
      setError(error.response?.data?.message || 'Failed to fetch business rules');
    } finally {
      setLoading(false);
    }
  };

  const handleTierChange = (tiers: CommissionTier[]) => {
    setPendingChanges(prev => ({
      ...prev,
      commission_tiers: tiers
    }));
  };

  const handleFeeChange = (feeType: 'manager_fee_percent' | 'company_overhead_percent', value: number) => {
    setPendingChanges(prev => ({
      ...prev,
      fees: {
        ...prev.fees,
        [feeType]: value
      }
    }));
  };

  const hasChanges = () => {
    return Object.keys(pendingChanges).length > 0;
  };

  const handlePreviewImpact = () => {
    if (!hasChanges()) {
      alert('No changes to preview');
      return;
    }
    setShowImpactAnalysis(true);
  };

  const handleApplyChanges = async (regenerateReports = false) => {
    if (!hasChanges()) return;

    try {
      const response = await apiService.post('/v2/admin/business-rules/apply-changes', {
        report_type: reportType,
        changes: pendingChanges,
        effective_date: effectiveDate,
        regenerate_reports: regenerateReports
      });

      alert(`Successfully applied changes. ${response.data.updated_rules} rules updated, ${response.data.regenerated_reports} reports regenerated.`);
      
      // Reset state
      setPendingChanges({});
      setShowImpactAnalysis(false);
      fetchCurrentRules();
    } catch (err) {
      const error = err as ApiError;
      setError(error.response?.data?.message || 'Failed to apply changes');
    }
  };

  const handleResetChanges = () => {
    setPendingChanges({});
    setShowImpactAnalysis(false);
  };

  if (user?.role !== 'admin') {
    return (
      <div className="p-6 bg-red-50 text-red-800 rounded-lg">
        <h2 className="text-xl font-semibold">Access Denied</h2>
        <p>You must be an administrator to access business rules management.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="bg-white shadow-lg rounded-lg">
        <div className="border-b border-gray-200 p-6">
          <h1 className="text-2xl font-bold text-gray-900">Business Rules Management</h1>
          <p className="mt-2 text-gray-600">Configure commission tiers, fees, and business calculation rules</p>
        </div>

        {error && (
          <div className="m-6 p-4 bg-red-50 text-red-800 rounded-lg">
            <p className="font-semibold">Error:</p>
            <p>{error}</p>
          </div>
        )}

        <div className="p-6 space-y-6">
          {/* Report Type Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Report Type
            </label>
            <div className="flex space-x-4">
              <button
                onClick={() => setReportType('exteriors')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  reportType === 'exteriors'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Exteriors Reports
              </button>
              <button
                onClick={() => setReportType('cody')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  reportType === 'cody'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Cody Reports
              </button>
            </div>
          </div>

          {/* Current Rules Display */}
          {currentRules && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-2">Current Rules</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Version:</span> {currentRules.version}
                </div>
                <div>
                  <span className="text-gray-600">Effective Date:</span> {currentRules.effective_date}
                </div>
              </div>
            </div>
          )}

          {/* Commission Tier Editor */}
          {reportType === 'exteriors' && currentRules && (
            <CommissionTierEditor
              tiers={pendingChanges.commission_tiers || currentRules.commission_tiers}
              onChange={handleTierChange}
              reportType={reportType}
            />
          )}

          {/* Fee Configuration */}
          {currentRules && (
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Fee Configuration</h3>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Manager Fee (%)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={pendingChanges.fees?.manager_fee_percent ?? currentRules.fees?.manager_fee_percent ?? 0}
                    onChange={(e) => handleFeeChange('manager_fee_percent', parseFloat(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    disabled={reportType === 'cody'}
                  />
                  {reportType === 'cody' && (
                    <p className="text-xs text-gray-500 mt-1">Cody reports have no manager fee</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Overhead (%)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={pendingChanges.fees?.company_overhead_percent ?? currentRules.fees?.company_overhead_percent ?? 0}
                    onChange={(e) => handleFeeChange('company_overhead_percent', parseFloat(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  {reportType === 'cody' && (
                    <p className="text-xs text-gray-500 mt-1">Note: Cody uses variable OH from CSV</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Effective Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Effective Date
            </label>
            <input
              type="date"
              value={effectiveDate}
              onChange={(e) => setEffectiveDate(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex justify-between items-center pt-6 border-t border-gray-200">
            <div className="flex space-x-4">
              <button
                onClick={handleResetChanges}
                disabled={!hasChanges()}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Reset Changes
              </button>
              <button
                onClick={() => setShowHistory(!showHistory)}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
              >
                View History
              </button>
            </div>
            <div className="flex space-x-4">
              <button
                onClick={handlePreviewImpact}
                disabled={!hasChanges()}
                className="px-4 py-2 text-blue-700 bg-blue-100 rounded-lg hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Preview Impact
              </button>
              <button
                onClick={() => handleApplyChanges(false)}
                disabled={!hasChanges()}
                className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Apply Changes
              </button>
            </div>
          </div>

          {/* Change Summary */}
          {hasChanges() && (
            <div className="mt-4 p-4 bg-yellow-50 rounded-lg">
              <h4 className="font-semibold text-yellow-800 mb-2">Pending Changes:</h4>
              <ul className="text-sm text-yellow-700 space-y-1">
                {pendingChanges.commission_tiers && (
                  <li>• Commission tier structure modified</li>
                )}
                {pendingChanges.fees?.manager_fee_percent !== undefined && (
                  <li>• Manager fee changed to {pendingChanges.fees.manager_fee_percent}%</li>
                )}
                {pendingChanges.fees?.company_overhead_percent !== undefined && (
                  <li>• Company overhead changed to {pendingChanges.fees.company_overhead_percent}%</li>
                )}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Impact Analysis Modal */}
      {showImpactAnalysis && currentRules && (
        <RuleChangeImpactAnalyzer
          reportType={reportType}
          currentRules={currentRules}
          proposedChanges={pendingChanges}
          onClose={() => setShowImpactAnalysis(false)}
          onApply={handleApplyChanges}
        />
      )}

      {/* History Viewer */}
      {showHistory && (
        <RuleHistoryViewer
          reportType={reportType}
          onClose={() => setShowHistory(false)}
        />
      )}
    </div>
  );
};

export default BusinessRulesManager;